// logic/integrationLogic/services/credentialVerificationService.js
import { integrationService } from './IntegrationService';
import { INTEGRATION_TYPES, SUPPLIED_BY } from '../config/IntegrationEndpoints';

/**
 * Credential Verification Service
 * Handles automatic verification of integration credentials on login
 * AWS/Bedrock use Organization config, Atlassian/XRAY use Integration endpoints
 */
class CredentialVerificationService {
  /**
   * Verify all configured integrations for a tenant
   * @param {string} tenantId - Organization/Tenant ID
   * @param {Function} onVerificationFail - Callback for verification failures (optional)
   * @returns {Promise<Object>} Verification results
   */
  async verifyAllCredentials(tenantId, onVerificationFail = null) {
    if (!tenantId) {
      console.error('❌ Tenant ID is required for credential verification');
      return {
        success: false,
        error: 'Tenant ID is required',
        results: {}
      };
    }

    console.log('🔍 Starting credential verification for tenant:', tenantId);

    try {
      // 🔥 NEW: Get AWS config for AWS/Bedrock verification
      const awsConfigResult = await integrationService.getAWSConfig(tenantId);
      
      // Get Atlassian/XRAY integrations from integration endpoint
      const integrationsResult = await integrationService.getAllIntegrations(tenantId);
      
      const verificationResults = {
        [INTEGRATION_TYPES.AWS]: { shouldVerify: false, verified: null, error: null },
        [INTEGRATION_TYPES.BEDROCK]: { shouldVerify: false, verified: null, error: null },
        [INTEGRATION_TYPES.ATLASSIAN]: { shouldVerify: false, verified: null, error: null },
        [INTEGRATION_TYPES.XRAY]: { shouldVerify: false, verified: null, error: null }
      };

      // 🔥 NEW: Verify AWS/Bedrock from organization config
      if (awsConfigResult.success && awsConfigResult.data) {
        const awsConfig = awsConfigResult.data;
        
        // ✅ AWS Verification
        // If customBuckets is false, they're using Tonic (ALWAYS VALID)
        // If customBuckets is true, they have custom credentials (ALWAYS VALID if saved)
        verificationResults[INTEGRATION_TYPES.AWS].shouldVerify = false; // No need to verify
        verificationResults[INTEGRATION_TYPES.AWS].verified = true; // Always valid
        console.log(`✅ AWS: ${awsConfig.customBuckets ? 'Using custom buckets' : 'Using Tonic buckets'} - VALID`);
        
        // ✅ Bedrock Verification
        // If customGenAi is false, they're using Tonic (ALWAYS VALID)
        // If customGenAi is true, they have custom credentials (ALWAYS VALID if saved)
        verificationResults[INTEGRATION_TYPES.BEDROCK].shouldVerify = false; // No need to verify
        verificationResults[INTEGRATION_TYPES.BEDROCK].verified = true; // Always valid
        console.log(`✅ BEDROCK: ${awsConfig.customGenAi ? 'Using custom Bedrock' : 'Using Tonic Bedrock'} - VALID`);
      } else {
        // If we can't fetch AWS config, assume using Tonic defaults (which are valid)
        console.log('⚠️ Could not fetch AWS config - assuming Tonic defaults (VALID)');
        verificationResults[INTEGRATION_TYPES.AWS].verified = true;
        verificationResults[INTEGRATION_TYPES.BEDROCK].verified = true;
      }

      // 🔥 EXISTING: Verify Atlassian/XRAY from integration endpoints
      if (!integrationsResult.success || !integrationsResult.data) {
        console.warn('⚠️ Failed to fetch integrations for Atlassian/XRAY verification');
      } else {
        const integrations = integrationsResult.data;
        
        // Check Atlassian and XRAY (optional integrations)
        for (const integrationType of [INTEGRATION_TYPES.ATLASSIAN, INTEGRATION_TYPES.XRAY]) {
          const integration = integrations.find(i => i.integrationType === integrationType);

          // Determine if we should verify this integration
          const shouldVerify = await this._shouldVerifyIntegration(
            integration, 
            integrationType
          );

          verificationResults[integrationType].shouldVerify = shouldVerify;

          if (shouldVerify) {
            console.log(`🔍 Verifying ${integrationType} credentials...`);
            
            try {
              const verifyResult = await integrationService.verifyCurrentIntegration(
                tenantId, 
                integrationType
              );

              verificationResults[integrationType].verified = verifyResult.success && verifyResult.verified;
              
              if (!verifyResult.success || !verifyResult.verified) {
                const errorMessage = verifyResult.error || 'Verification failed';
                verificationResults[integrationType].error = errorMessage;
                console.error(`❌ ${integrationType} verification failed:`, errorMessage);
                
                // 🔥 Trigger notification for invalid optional credentials
                if (onVerificationFail) {
                  onVerificationFail({
                    type: 'CRITICAL',
                    integrationType,
                    title: `${integrationType} Credentials Invalid`,
                    description: `Failed to verify ${integrationType} credentials. ${errorMessage}\nYou have supplied credentials for this integration but they are invalid. Please update your credentials immediately.`,
                    actionUrl: '/integration-setup',
                    sourceId: `${integrationType}_verification`
                  });
                }
              } else {
                console.log(`✅ ${integrationType} credentials verified successfully`);
              }
            } catch (error) {
              console.error(`❌ Error verifying ${integrationType}:`, error);
              verificationResults[integrationType].verified = false;
              verificationResults[integrationType].error = error.message || 'Verification error';
              
              if (onVerificationFail) {
                onVerificationFail({
                  type: 'CRITICAL',
                  integrationType,
                  title: `${integrationType} Verification Error`,
                  description: `Error verifying ${integrationType} credentials: ${error.message || 'Unknown error'}\nYou have supplied credentials for this integration but they are invalid. Please update your credentials immediately.`,
                  actionUrl: '/integration-setup',
                  sourceId: `${integrationType}_verification`
                });
              }
            }
          } else {
            console.log(`⏭️ Skipping verification for ${integrationType}`);
          }
        }
      }

      // Determine overall success
      // 🔥 AWS/Bedrock are ALWAYS valid (using Tonic or custom from org config)
      // Only Atlassian/XRAY can cause failures
      const hasFailures = Object.values(verificationResults).some(
        result => result.shouldVerify && result.verified === false
      );

      return {
        success: !hasFailures,
        results: verificationResults,
        hasFailures
      };

    } catch (error) {
      console.error('❌ Error in credential verification:', error);
      return {
        success: false,
        error: error.message || 'Verification failed',
        results: {}
      };
    }
  }

  /**
   * Determine if an integration should be verified
   * @private
   */
  async _shouldVerifyIntegration(integration, integrationType) {
    // 🔥 AWS/Bedrock are NEVER verified here (handled separately via org config)
    if (integrationType === INTEGRATION_TYPES.AWS || integrationType === INTEGRATION_TYPES.BEDROCK) {
      return false;
    }
    
    // For optional integrations (Atlassian, XRAY)
    if (!integration) {
      console.log(`⏭️ Optional integration ${integrationType} doesn't exist`);
      return false;
    }

    // Check if ANY credential was supplied using the "Supplied" flag fields
    if (integrationType === INTEGRATION_TYPES.ATLASSIAN) {
      const hasActualCredentials = 
        (integration.emailSupplied && integration.emailSupplied !== 'not_supplied') ||
        (integration.apiTokenSupplied && integration.apiTokenSupplied !== 'not_supplied') ||
        (integration.siteUrlSupplied && integration.siteUrlSupplied !== 'not_supplied');
      
      if (!hasActualCredentials) {
        console.log(`⏭️ ${integrationType} - No credentials provided. This is OK, skipping verification.`);
        return false;
      }
      
      console.log(`🔍 ${integrationType} - Credentials detected. MUST verify for validity.`);
      return true;
    }

    if (integrationType === INTEGRATION_TYPES.XRAY) {
      const hasActualCredentials = 
        (integration.clientIdSupplied && integration.clientIdSupplied !== 'not_supplied') ||
        (integration.clientSecretSupplied && integration.clientSecretSupplied !== 'not_supplied');
      
      if (!hasActualCredentials) {
        console.log(`⏭️ ${integrationType} - No credentials provided. This is OK, skipping verification.`);
        return false;
      }
      
      console.log(`🔍 ${integrationType} - Credentials detected. MUST verify for validity.`);
      return true;
    }

    return false;
  }

  /**
   * Generate user-friendly notification messages for verification failures
   * @param {Object} verificationResults - Results from verifyAllCredentials
   * @returns {Array} Array of notification objects
   */
  generateNotifications(verificationResults) {
    const notifications = [];

    if (!verificationResults.results) {
      return notifications;
    }

    // Check each integration (only Atlassian/XRAY can fail now)
    Object.entries(verificationResults.results).forEach(([integrationType, result]) => {
      if (result.shouldVerify && result.verified === false) {
        notifications.push({
          type: 'CRITICAL',
          title: `${integrationType} Credentials Invalid`,
          description: `Failed to verify ${integrationType} credentials. You have supplied credentials for this integration but they are invalid. Please update your credentials immediately.${result.error ? `\nError: ${result.error}` : ''}`,
          actionUrl: '/integration-setup',
          sourceId: `${integrationType}_verification`
        });
      }
    });

    return notifications;
  }

  /**
   * Get a summary of verification results for logging/display
   * @param {Object} verificationResults - Results from verifyAllCredentials
   * @returns {Object} Summary object
   */
  getSummary(verificationResults) {
    if (!verificationResults.results) {
      return {
        total: 0,
        verified: 0,
        failed: 0,
        skipped: 0
      };
    }

    const results = verificationResults.results;
    const summary = {
      total: 0,
      verified: 0,
      failed: 0,
      skipped: 0,
      details: {}
    };

    Object.entries(results).forEach(([integrationType, result]) => {
      if (result.shouldVerify) {
        summary.total++;
        if (result.verified === true) {
          summary.verified++;
          summary.details[integrationType] = 'verified';
        } else if (result.verified === false) {
          summary.failed++;
          summary.details[integrationType] = 'failed';
        }
      } else {
        summary.skipped++;
        summary.details[integrationType] = 'skipped';
      }
    });

    return summary;
  }
}

// Create and export as both named and default
const credentialVerificationServiceInstance = new CredentialVerificationService();

// Named export
export const credentialVerificationService = credentialVerificationServiceInstance;

// Default export (for compatibility)
export default credentialVerificationServiceInstance;